import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { MilestoneService } from '../services/milestoneService';
import type { Milestone, UpdateMilestoneData, BenefitData } from '../types/milestone';

const MilestoneDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [milestone, setMilestone] = useState<Milestone | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<UpdateMilestoneData>({});
  const [editingBenefitIndex, setEditingBenefitIndex] = useState<number | null>(null);
  const [benefitDraft, setBenefitDraft] = useState<BenefitData | null>(null);
  const [showAddBenefit, setShowAddBenefit] = useState(false);
  const [newBenefit, setNewBenefit] = useState<BenefitData>({
    benefitName: '',
    details: '',
    value: 0,
    category: '',
    isActive: true,
  });

  useEffect(() => {
    if (id) {
      fetchMilestone(id);
    }
  }, [id]);

  const fetchMilestone = async (milestoneId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await MilestoneService.getMilestoneById(milestoneId);
      setMilestone(response);
      setFormData({
        description: response.description || '',
        rps: response.rps || 0,
        isActive: response.isActive ?? true,
        spendThreshold: response.spendThreshold || 0,
        lastDateToAvail: response.lastDateToAvail || null,
        validFrom: response.validFrom || null,
        validUntil: response.validUntil || null,
        spendConditions: response.spendConditions || []
      });
      document.title = `${response.description} Milestone | Rewards Admin`;
    } catch (err) {
      console.error('Error fetching milestone:', err);
      setError(err instanceof Error ? err.message : 'Failed to load milestone');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (milestone) {
      setFormData({
        description: milestone.description || '',
        rps: milestone.rps || 0,
        isActive: milestone.isActive ?? true,
        spendThreshold: milestone.spendThreshold || 0,
        lastDateToAvail: milestone.lastDateToAvail || null,
        validFrom: milestone.validFrom || null,
        validUntil: milestone.validUntil || null,
        spendConditions: milestone.spendConditions || []
      });
    }
  };

  const handleSave = async () => {
    if (!milestone) return;

    try {
      setIsSaving(true);
      setError(null);
      await MilestoneService.updateMilestone(milestone._id, formData);
      await fetchMilestone(milestone._id);
      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating milestone:', err);
      setError(err instanceof Error ? err.message : 'Failed to update milestone');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConditionChange = (index: number, field: 'min' | 'max' | 'description', value: any) => {
    setFormData(prev => {
      const list = [...(prev.spendConditions || [])];
      list[index] = {
        ...list[index],
        [field]: field === 'min' || field === 'max' ? Number(value) : value
      };
      return { ...prev, spendConditions: list };
    });
  };

  const addCondition = () => {
    setFormData(prev => {
      const list = [...(prev.spendConditions || [])];
      list.push({ min: 0, max: 0, description: '' });
      return { ...prev, spendConditions: list };
    });
  };

  const removeCondition = (index: number) => {
    setFormData(prev => {
      const list = [...(prev.spendConditions || [])];
      list.splice(index, 1);
      return { ...prev, spendConditions: list };
    });
  };

  const handleBenefitAdd = async (benefitData: BenefitData) => {
    if (!milestone) return;

    try {
      await MilestoneService.addBenefitToMilestone(milestone._id, benefitData);
      await fetchMilestone(milestone._id);
    } catch (err) {
      console.error('Error adding benefit:', err);
      setError(err instanceof Error ? err.message : 'Failed to add benefit');
    }
  };

  const handleBenefitUpdate = async (benefitIndex: number, benefitData: BenefitData) => {
    if (!milestone) return;

    try {
      await MilestoneService.updateBenefitInMilestone(milestone._id, benefitIndex, benefitData);
      await fetchMilestone(milestone._id);
      setEditingBenefitIndex(null);
      setBenefitDraft(null);
    } catch (err) {
      console.error('Error updating benefit:', err);
      setError(err instanceof Error ? err.message : 'Failed to update benefit');
    }
  };

  const handleBenefitDelete = async (benefitIndex: number) => {
    if (!milestone) return;

    try {
      await MilestoneService.deleteBenefitFromMilestone(milestone._id, benefitIndex);
      await fetchMilestone(milestone._id);
    } catch (err) {
      console.error('Error deleting benefit:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete benefit');
    }
  };

  const startEditBenefit = (index: number) => {
    if (!milestone) return;
    const b = milestone.benefits[index];
    setEditingBenefitIndex(index);
    setBenefitDraft({
      benefitName: b.benefitName,
      details: b.details,
      value: b.value,
      category: b.category,
      isActive: b.isActive
    });
  };

  const cancelEditBenefit = () => {
    setEditingBenefitIndex(null);
    setBenefitDraft(null);
  };

  const saveEditBenefit = async () => {
    if (editingBenefitIndex === null || !benefitDraft) return;
    await handleBenefitUpdate(editingBenefitIndex, benefitDraft);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getMilestoneTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      welcome: 'bg-green-100 text-green-800',
      monthly: 'bg-blue-100 text-blue-800',
      quarterly: 'bg-purple-100 text-purple-800',
      annually: 'bg-yellow-100 text-yellow-800',
      half_yearly: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
        {error}
      </div>
    );
  }

  if (!milestone) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Milestone not found</h2>
        <p className="mt-2 text-gray-500">The milestone you're looking for doesn't exist.</p>
      </div>
    );
  }
  const resolveCardDetailPath = (): string => {
    // Prefer explicit referrer from navigation state
    const from = (location.state as any)?.from as string | undefined;
    if (from) return from;
    // Fallback to card detail based on milestone fields
    if (milestone) {
      const cardIdField = milestone.cardId as any;
      let cardId: string | undefined;
      if (typeof cardIdField === 'string') {
        cardId = cardIdField;
      } else if (cardIdField && typeof cardIdField === 'object' && cardIdField._id) {
        cardId = cardIdField._id;
      }
      if (cardId) return `/cards/${cardId}`;
      if (milestone.cardSlug) return `/cards/${milestone.cardSlug}`;
    }
    // Default back to milestones list
    return '/milestones';
  };

  const backPath = resolveCardDetailPath();
  const backLabel = backPath.startsWith('/cards/') ? 'Back to Card' : 'Back to Milestones';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={backPath}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {backLabel}
          </Link>
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Milestone' : 'Milestone Details'}
            </h1>
            <p className="text-gray-600">
              {milestone.bankName} • {milestone.cardName}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Edit Milestone
            </button>
          )}
        </div>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4">
          Milestone updated successfully!
        </div>
      )}

      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              {isEditing ? (
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              ) : (
                <p className="text-gray-900">{milestone.description}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Milestone Type</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getMilestoneTypeColor(milestone.milestoneType)}`}>
                {milestone.milestoneType.charAt(0).toUpperCase() + milestone.milestoneType.slice(1).replace('_', ' ')}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Spend Threshold</label>
              {isEditing ? (
                <input
                  type="number"
                  value={formData.spendThreshold || 0}
                  onChange={(e) => handleInputChange('spendThreshold', Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formatCurrency(milestone.spendThreshold)}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reward Points</label>
              {isEditing ? (
                <input
                  type="number"
                  value={formData.rps || 0}
                  onChange={(e) => handleInputChange('rps', Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{milestone.rps.toLocaleString()} RPs</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Date to Avail</label>
              {isEditing ? (
                <select
                  value={formData.lastDateToAvail || ''}
                  onChange={(e) => handleInputChange('lastDateToAvail', e.target.value === '' ? null : e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="null">No limit</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half_yearly">Half Yearly</option>
                  <option value="annually">Annually</option>
                </select>
              ) : (
                <p className="text-gray-900">{milestone.lastDateToAvail || 'No limit'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              {isEditing ? (
                <select
                  value={formData.isActive ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              ) : (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                  milestone.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {milestone.isActive ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Spend Conditions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Spend Conditions</h3>
          {isEditing && (
            <button
              onClick={addCondition}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Add Condition
            </button>
          )}
        </div>
        <div className="p-6">
          {(!milestone.spendConditions || milestone.spendConditions.length === 0) && !isEditing ? (
            <div className="text-sm text-gray-500">No spend conditions.</div>
          ) : (
            <div className="space-y-4">
              {(isEditing ? (formData.spendConditions || []) : (milestone.spendConditions || [])).map((condition, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {isEditing ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Min</label>
                        <input
                          type="number"
                          value={condition.min}
                          onChange={(e) => handleConditionChange(index, 'min', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min={0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max</label>
                        <input
                          type="number"
                          value={condition.max}
                          onChange={(e) => handleConditionChange(index, 'max', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min={0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          value={condition.description || ''}
                          onChange={(e) => handleConditionChange(index, 'description', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Optional description"
                        />
                      </div>
                      <div className="md:col-span-3 flex justify-end">
                        <button
                          onClick={() => removeCondition(index)}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Min: {condition.min} • Max: {condition.max}</p>
                        {condition.description && (
                          <p className="text-sm text-gray-500">{condition.description}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-medium text-gray-900">Benefits</h3>
            <span className="text-sm text-gray-500">
              {milestone.benefits.length} benefit{milestone.benefits.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={() => setShowAddBenefit(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Add Benefit
          </button>
        </div>
        <div className="p-6">
          {milestone.benefits.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="mt-4 text-sm font-medium text-gray-900">No benefits</h4>
              <p className="mt-1 text-sm text-gray-500">This milestone doesn't have any benefits yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {milestone.benefits.map((benefit, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  {editingBenefitIndex === index && benefitDraft ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Name</label>
                          <input
                            type="text"
                            value={benefitDraft.benefitName}
                            onChange={(e) => setBenefitDraft({ ...(benefitDraft as BenefitData), benefitName: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Value (₹)</label>
                          <input
                            type="number"
                            value={benefitDraft.value}
                            onChange={(e) => setBenefitDraft({ ...(benefitDraft as BenefitData), value: Number(e.target.value) })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min={0}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                          <input
                            type="text"
                            value={benefitDraft.details}
                            onChange={(e) => setBenefitDraft({ ...(benefitDraft as BenefitData), details: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <input
                            type="text"
                            value={benefitDraft.category || ''}
                            onChange={(e) => setBenefitDraft({ ...(benefitDraft as BenefitData), category: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Active</label>
                          <select
                            value={benefitDraft.isActive ? 'true' : 'false'}
                            onChange={(e) => setBenefitDraft({ ...(benefitDraft as BenefitData), isActive: e.target.value === 'true' })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex items-center justify-end space-x-2">
                        <button onClick={cancelEditBenefit} className="px-3 py-1.5 text-sm border rounded-md text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                        <button onClick={saveEditBenefit} className="px-3 py-1.5 text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700">Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{benefit.benefitName}</h4>
                        <p className="text-sm text-gray-500 mt-1">{benefit.details}</p>
                        {benefit.category && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                            {benefit.category}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(benefit.value)}</span>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => startEditBenefit(index)}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this benefit?')) {
                                handleBenefitDelete(index);
                              }
                            }}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Benefit Modal */}
      {showAddBenefit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-30" onClick={() => setShowAddBenefit(false)}></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Add Benefit</h3>
              <button onClick={() => setShowAddBenefit(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Name</label>
                  <input
                    type="text"
                    value={newBenefit.benefitName}
                    onChange={(e) => setNewBenefit(prev => ({ ...prev, benefitName: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value (₹)</label>
                  <input
                    type="number"
                    min={0}
                    value={newBenefit.value}
                    onChange={(e) => setNewBenefit(prev => ({ ...prev, value: Number(e.target.value) }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                <input
                  type="text"
                  value={newBenefit.details}
                  onChange={(e) => setNewBenefit(prev => ({ ...prev, details: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={newBenefit.category || ''}
                    onChange={(e) => setNewBenefit(prev => ({ ...prev, category: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Active</label>
                  <select
                    value={newBenefit.isActive ? 'true' : 'false'}
                    onChange={(e) => setNewBenefit(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowAddBenefit(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleBenefitAdd(newBenefit);
                  setShowAddBenefit(false);
                  setNewBenefit({ benefitName: '', details: '', value: 0, category: '', isActive: true });
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Card Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              <p className="text-gray-900">{milestone.bankName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Name</label>
              <p className="text-gray-900">{milestone.cardName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Slug</label>
              <p className="text-gray-900">{milestone.bankSlug}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Slug</label>
              <p className="text-gray-900">{milestone.cardSlug}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Metadata</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
              <p className="text-gray-900">
                {new Date(milestone.createdAt).toLocaleString()}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Updated At</label>
              <p className="text-gray-900">
                {new Date(milestone.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilestoneDetail;
